"use client";

import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { type ReactNode, useEffect, useRef, useState } from "react";

// Custom hook to handle reduced motion with SSR fallback
function useReducedMotionWithFallback() {
  const [isClient, setIsClient] = useState(false);
  const frameworkReducedMotion = useReducedMotion();

  // Fallback for SSR using matchMedia
  const [fallbackReducedMotion, setFallbackReducedMotion] = useState(false);

  useEffect(() => {
    setIsClient(true);

    // Fallback detection for SSR compatibility
    if (typeof window !== "undefined" && window.matchMedia) {
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      setFallbackReducedMotion(mediaQuery.matches);

      const handleChange = (e: MediaQueryListEvent) => {
        setFallbackReducedMotion(e.matches);
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, []);

  // Use framer-motion's hook if available, otherwise use fallback
  return isClient ? (frameworkReducedMotion ?? fallbackReducedMotion) : false;
}

export interface MotionWrapperProps {
  children: ReactNode;
  className?: string;

  // Animation configuration
  variant?:
    | "fadeIn"
    | "slideUp"
    | "slideDown"
    | "slideLeft"
    | "slideRight"
    | "scale";
  delay?: number;
  duration?: number;
  y?: number;
  x?: number;
  scale?: number;

  // Viewport configuration
  threshold?: number;
  margin?: string;
  once?: boolean;

  // Scroll-based animations
  enableScrollAnimation?: boolean;
  scrollYProgress?: [number, number];
  scrollYRange?: [number, number];

  // Stagger children animations
  staggerChildren?: number;
  staggerDirection?: 1 | -1;
}

// Predefined animation variants
const animationVariants = {
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  slideUp: {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  },
  slideDown: {
    hidden: { opacity: 0, y: -60 },
    visible: { opacity: 1, y: 0 },
  },
  slideLeft: {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0 },
  },
  slideRight: {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0 },
  },
  scale: {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
  },
} as const;

export function MotionWrapper({
  children,
  className,
  variant = "fadeIn",
  delay = 0,
  duration = 0.6,
  y,
  x,
  scale: scaleValue,
  threshold = 0.1,
  margin = "0px 0px -100px 0px",
  once = true,
  enableScrollAnimation = false,
  scrollYProgress = [0, 1],
  scrollYRange = [0, 100],
  staggerChildren,
  staggerDirection = 1,
}: MotionWrapperProps) {
  const ref = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotionWithFallback();

  // Always call hooks to avoid conditional hook calls
  const { scrollYProgress: scrollProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const scrollY = useTransform(scrollProgress, scrollYProgress, scrollYRange);

  // If reduced motion is preferred, return a plain div without animations
  if (shouldReduceMotion) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  // Get base animation variant
  const baseVariant = animationVariants[variant] || animationVariants.fadeIn;

  // Build initial state
  const initialState = {
    ...baseVariant.hidden,
    ...(y !== undefined && { y }),
    ...(x !== undefined && { x }),
    ...(scaleValue !== undefined && { scale: scaleValue }),
  };

  // Build animate state
  const animateState = {
    ...baseVariant.visible,
    y: 0,
    x: 0,
    scale: 1,
  };

  // Build transition - use zero duration if reduced motion is preferred
  const transitionConfig = {
    duration: shouldReduceMotion ? 0 : duration,
    delay: shouldReduceMotion ? 0 : delay,
    ease: [0.25, 0.25, 0, 1] as const,
    ...(staggerChildren &&
      !shouldReduceMotion && {
        staggerChildren: staggerChildren * staggerDirection,
      }),
  };

  // For scroll-based animations, add scroll transforms (disabled for reduced motion)
  const scrollStyle =
    enableScrollAnimation && !shouldReduceMotion
      ? {
          y: scrollY,
        }
      : {};

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={shouldReduceMotion ? animateState : initialState}
      whileInView={animateState}
      viewport={{
        amount: threshold,
        margin,
        once,
      }}
      transition={transitionConfig}
      style={scrollStyle}
    >
      {children}
    </motion.div>
  );
}
